import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIssuers, useBadges, useBadge, useCreateBadge, useCreateTemplate, useTemplates, useDrafts, useCreateDraft, useUpdateDraft, useDeleteDraft, useUpdateBadge, useUpdateBadgeShareText, useDeleteBadge, useAwards, useUsage, useSaveBadgeCertificate, useIssuerSettings } from '../hooks/useApi';
import { BadgeDesigner, type BadgeDesignerHandle } from '../components/BadgeDesigner';
import { buildCertificateSvg, DEFAULT_CERTIFICATE_OPTIONS } from '../lib/certificateTemplates';
import { api } from '../api/client';
import { track } from '../hooks/usePageTracker';
import { ConfirmModal } from '../components/ConfirmModal';
import { IssuerPicker } from '../components/IssuerPicker';
import { LimitWarning, isAtLimit } from '../components/LimitWarning';
import { ActionErrorPanel } from '../components/ActionErrorPanel';
import { CertificateDesigner } from '../components/CertificateDesigner';
import type { Badge, Issuer } from '../types';

// All backend write/lookup paths take the GUID in the URL path. Sending a full
// URL there breaks API Gateway HTTP API routing because %2F gets decoded
// before route matching. Backend resolvers reconstruct the full DDB key (with
// legacy-domain fallback) from the GUID.
function badgeGuid(badge: Badge): string {
  return badge.id.split('/').pop() || badge.id;
}

export function BadgesPage() {
  const { t } = useTranslation('dashboard');
  const { data: issuers, isLoading: issuersLoading } = useIssuers();
  const { data: badges, isLoading: badgesLoading } = useBadges();
  const { mutate: createBadge, isPending: creating } = useCreateBadge();
  const { mutate: saveCertificate } = useSaveBadgeCertificate();
  const { mutate: saveTemplate } = useCreateTemplate();
  const { mutate: updateShareText, isPending: updatingShare } = useUpdateBadgeShareText();
  const { mutate: deleteBadge } = useDeleteBadge();
  const updateBadge = useUpdateBadge();
  const { data: usage } = useUsage();
  const atBadgeLimit = isAtLimit(usage, 'badges');
  const [createError, setCreateError] = useState<Error | null>(null);
  const [lastCreatePayload, setLastCreatePayload] = useState<Record<string, unknown> | null>(null);

  const [issuerFilter, setIssuerFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const [certBadge, setCertBadge] = useState<Badge | null>(null);
  const [certTipDismissed, setCertTipDismissed] = useState(() => {
    try { return localStorage.getItem('certTipDismissed') === '1'; } catch { return false; }
  });
  function dismissCertTip() {
    setCertTipDismissed(true);
    try { localStorage.setItem('certTipDismissed', '1'); } catch { /* ignore */ }
  }
  const [editBadge, setEditBadge] = useState<Badge | null>(null);

  // Form state (shared between create + edit)
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [criteriaText, setCriteriaText] = useState('');
  const [selectedIssuer, setSelectedIssuer] = useState('');
  // Brand color of the issuer chosen in the create form — used as the default
  // accent for the badge's auto-created certificate.
  const { data: selectedIssuerSettings } = useIssuerSettings(selectedIssuer ? (selectedIssuer.split('/').pop() ?? null) : null);
  const [shareText, setShareText] = useState('');
  // Tracks whether the user has manually edited share text. When false, the
  // share text auto-syncs with the generated template as name/issuer change.
  const [shareTextTouched, setShareTextTouched] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  // A design carried over from the public /designer flow (Edit this template →
  // Sign up to save). Rehydrated into the create modal, then cleared.
  const [pendingDesignJson, setPendingDesignJson] = useState<string | null>(null);
  // Draft + auto-save: a badge-in-progress persisted server-side (cross-device),
  // listed in the badges grid. Once saved once, auto-save keeps it current.
  const [draftSaved, setDraftSaved] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const designerRef = useRef<BadgeDesignerHandle | null>(null);
  const { data: templatesData } = useTemplates();
  const { data: draftsData } = useDrafts();
  const { mutateAsync: createDraft } = useCreateDraft();
  const { mutateAsync: updateDraft } = useUpdateDraft();
  const { mutate: deleteDraft } = useDeleteDraft();
  const drafts = draftsData?.items ?? [];

  // When editing, fetch the award count for the badge so we can lock the form
  // if awards already exist (preserves award integrity).
  const editBadgeIdForApi = editBadge ? badgeGuid(editBadge) : undefined;
  // Fetch the full badge document (incl. designJson) when entering edit mode.
  // The list endpoint omits designJson to keep responses lean.
  const { data: editBadgeFull } = useBadge(editBadgeIdForApi);
  const { data: awardsData, isLoading: awardsLoading } = useAwards(
    editBadgeIdForApi ? { badgeId: editBadgeIdForApi } : undefined,
    undefined,
    !!editBadge
  );
  const hasAwards = !!awardsData && (awardsData.count > 0 || awardsData.items.length > 0);
  // Issued awards embed a snapshot of the badge (see buildOpenBadgeAward), so
  // editing the badge class never mutates already-issued awards — it only
  // affects future ones. We therefore allow editing awarded badges (just lock
  // briefly while the award count loads) instead of blocking outright.
  const locked = !!editBadge && awardsLoading;

  const issuerList = useMemo(() => {
    if (!issuers) return [];
    return Array.isArray(issuers) ? issuers : ((issuers as any).items ?? []);
  }, [issuers]);

  const badgeList = useMemo(() => {
    if (!badges) return [];
    return Array.isArray(badges) ? badges : ((badges as any).items ?? []);
  }, [badges]);

  const verifiedIssuers = useMemo(() => issuerList.filter((i: Issuer) => i.verified), [issuerList]);

  // Default share-text template — localized to the issuer's UI language.
  // Uses {{...}} placeholders that the backend (Public_certify.mjs)
  // substitutes per-recipient at share time. Because i18next ALSO uses
  // {{var}} for interpolation, we pass the literal tokens as interpolation
  // values so they survive verbatim in the resolved string.
  const defaultShareText = t('badges.defaultShareText', {
    defaultValue: `Excited to share that I just received the "{{badge_name}}" certification!\n\nThanks to {{issuer_name}} for this awesome recognition.\n\n{{award_url}}`,
    badge_name: '{{badge_name}}',
    issuer_name: '{{issuer_name}}',
    award_url: '{{award_url}}',
  });

  // Seed the default share text when the create modal opens (and the user
  // hasn't manually edited it). The template is static — recipient/badge/issuer
  // values are substituted at share time on the backend, not here.
  useEffect(() => {
    if (showCreate && !editBadge && !shareTextTouched && !shareText) {
      setShareText(defaultShareText);
    }
  }, [showCreate, editBadge, shareTextTouched, shareText, defaultShareText]);

  // Auto-save the draft while the create modal is open and auto-save is on:
  // debounced on form changes, plus a periodic tick to capture canvas edits
  // (which live in the Fabric ref, not React state).
  useEffect(() => {
    if (!autoSave || !showCreate || editBadge) return;
    const debounce = setTimeout(() => saveDraft(false), 700);
    const interval = setInterval(() => saveDraft(false), 20000);
    return () => { clearTimeout(debounce); clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSave, showCreate, editBadge, name, description, criteriaText, selectedIssuer, shareText]);

  // Rehydrate a design carried over from the public /designer flow: open the
  // create modal pre-loaded with the stashed canvas, then clear the handoff.
  useEffect(() => {
    let raw: string | null = null;
    try { raw = localStorage.getItem('bws:pendingBadgeDesign'); } catch { /* ignore */ }
    if (!raw) return;
    try {
      const { designJson } = JSON.parse(raw) as { designJson?: string };
      if (designJson) { setPendingDesignJson(designJson); openCreate(); }
    } catch { /* malformed — drop it */ }
    try { localStorage.removeItem('bws:pendingBadgeDesign'); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Placeholder tokens supported in share text. Kept in sync with the
  // substitution table in BWS-BADGES-Public_certify.mjs.
  const SHARE_PLACEHOLDERS: { token: string; label: string; example: string }[] = [
    { token: '{{recipient_name}}', label: t('badges.phRecipientName', "Recipient's name"), example: 'Alice Johnson' },
    { token: '{{badge_name}}', label: t('badges.phBadgeName', 'Badge name'), example: name.trim() || 'Python Certified' },
    { token: '{{issuer_name}}', label: t('badges.phIssuerName', 'Issuer name'), example: verifiedIssuers.find((i: Issuer) => i.id === selectedIssuer)?.name || 'Acme Inc' },
    { token: '{{date}}', label: t('badges.phDate', 'Issue date'), example: 'April 17, 2026' },
    { token: '{{award_url}}', label: t('badges.phAwardUrl', 'Public award URL'), example: 'https://badges.ninja/awards/...' },
  ];

  const shareTextRef = useRef<HTMLTextAreaElement>(null);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  function insertPlaceholder(token: string) {
    const ta = shareTextRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? shareText.length;
    const end = ta.selectionEnd ?? shareText.length;
    const newValue = shareText.slice(0, start) + token + shareText.slice(end);
    setShareText(newValue);
    setShareTextTouched(true);
    setShowPlaceholders(false);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  const filteredBadges = useMemo(() => {
    if (issuerFilter === 'all') return badgeList;
    return badgeList.filter((b: Badge) => b.issuer?.id === issuerFilter);
  }, [badgeList, issuerFilter]);

  function closeModal() {
    setShowCreate(false);
    setPendingDesignJson(null);
    setEditBadge(null);
    setModalError(null);
    setDraftSaved(false);
    setAutoSave(false);
    setDraftId(null);
  }

  // Gather the current create-flow state for a draft payload.
  function draftPayload() {
    let designJson: string | undefined;
    try { designJson = designerRef.current?.onExportJSON() || undefined; } catch { /* ignore */ }
    return {
      name, description, criteria: criteriaText, issuerId: selectedIssuer,
      shareText, shareTextTouched, designJson,
    };
  }

  // Save the draft server-side. First save creates it (and turns on auto-save);
  // later saves update the same draft.
  async function saveDraft(manual = true) {
    if (editBadge) return; // drafts are for new badges only
    if (draftBusy) return;
    setDraftBusy(true);
    try {
      if (draftId) {
        await updateDraft({ id: draftId, data: draftPayload() });
      } else {
        const res = await createDraft(draftPayload());
        setDraftId(res.draftId);
      }
      setDraftSaved(true);
      if (manual && !autoSave) setAutoSave(true);
    } catch { /* transient — the next auto-save/tick retries */ }
    finally { setDraftBusy(false); }
  }

  // Resume a saved draft from the badges grid: load its fields + design.
  async function openDraft(id: string) {
    try {
      const d = await api.getDraft(id);
      setEditBadge(null);
      setName(d.name || '');
      setDescription(d.description || '');
      setCriteriaText(d.criteria || '');
      setSelectedIssuer(d.issuerId || '');
      setShareText(d.shareText || '');
      setShareTextTouched(!!d.shareTextTouched);
      setPendingDesignJson(d.designJson ?? null);
      setDraftId(d.id);
      setDraftSaved(true);
      setAutoSave(true);
      setShowCreate(true);
    } catch { /* ignore */ }
  }

  function openCreate() {
    setEditBadge(null);
    setName('');
    setDescription('');
    setCriteriaText('');
    setShareText('');
    setShareTextTouched(false);
    setSelectedIssuer(verifiedIssuers.length === 1 ? verifiedIssuers[0].id : '');
    setDraftSaved(false); setAutoSave(false); setDraftId(null);
    setShowCreate(true);
  }

  function openEdit(badge: Badge) {
    setEditBadge(badge);
    setName(badge.name || '');
    setDescription(badge.description || '');
    const criteria = badge.criteria?.narrative;
    setCriteriaText(Array.isArray(criteria) ? criteria.join('\n') : (criteria || ''));
    setSelectedIssuer(badge.issuer?.id || '');
    setShareText(badge.shareText || '');
    // In edit mode the saved value is user-authored, so prevent template auto-sync.
    setShareTextTouched(true);
    setShowCreate(true);
  }

  async function handleCreate() {
    if (!name.trim() || !selectedIssuer) return;
    track('badge_create_click');
    let image = '';
    let designJson: string | undefined;
    let designSvg: string | undefined;
    if (designerRef.current) {
      image = await designerRef.current.onExport();
      try { designJson = designerRef.current.onExportJSON() || undefined; } catch { /* ignore */ }
      // A re-renderable SVG is only useful when the design carries merge tokens
      // ({{recipient_name}} etc.) — the server rasterizes it per recipient.
      try {
        const svg = await designerRef.current.onExportSVG();
        if (svg && svg.includes('{{')) designSvg = svg;
      } catch { /* ignore */ }
    }
    // Guard the two required fields the modal didn't previously enforce, with a
    // specific message — the backend otherwise returns a confusing 400 that
    // lists every parameter (reported support issue). criteria is often left
    // blank; image is empty when the designer canvas hasn't finished exporting.
    if (!description.trim()) {
      track('badge_create_blocked', { reason: 'description' });
      setCreateError(new Error(t('badges.descriptionRequired', 'Please add a description — what this badge represents.')));
      return;
    }
    if (!criteriaText.trim()) {
      track('badge_create_blocked', { reason: 'criteria' });
      setCreateError(new Error(t('badges.criteriaRequired', 'Please add the completion criteria — what someone must do to earn this badge.')));
      return;
    }
    if (!image) {
      track('badge_create_blocked', { reason: 'image' });
      setCreateError(new Error(t('badges.imageRequired', 'Your badge image isn’t ready yet. Pick a template (or add an element) in the designer on the left, then try again.')));
      return;
    }
    const payload = {
      name: name.trim(),
      description: description.trim(),
      criteria: criteriaText.trim(),
      image: '<image data omitted>',
      issuerId: selectedIssuer,
      shareText: shareText.trim() || undefined,
    };
    setLastCreatePayload(payload);
    setCreateError(null);
    createBadge(
      {
        name: name.trim(),
        description: description.trim(),
        criteria: criteriaText.trim(),
        image,
        issuerId: selectedIssuer,
        shareText: shareText.trim() || undefined,
        designJson,
        designSvg,
      },
      {
        onSuccess: (res) => {
          track('badge_create_success');
          // Every badge gets a default certificate so recipients can download a
          // designed PDF out of the box; the owner customizes it via the
          // Certificate designer. Fire-and-forget — never block badge creation.
          const newBadgeId = (res as { badgeId?: string } | undefined)?.badgeId;
          if (newBadgeId) {
            const accentColor = selectedIssuerSettings?.brandColor || DEFAULT_CERTIFICATE_OPTIONS.accentColor;
            saveCertificate({
              badgeId: newBadgeId.split('/').pop() as string,
              certificateSvg: buildCertificateSvg({ ...DEFAULT_CERTIFICATE_OPTIONS, accentColor }),
            });
          }
          // Save the design as a reusable account template (fire-and-forget so a
          // template failure never blocks badge creation).
          if (designJson) saveTemplate({ name: name.trim(), designJson, image });
          // The badge is created — its draft (if any) is no longer needed.
          if (draftId) deleteDraft(draftId);
          setDraftSaved(false); setAutoSave(false); setDraftId(null);
          setShowCreate(false); setPendingDesignJson(null);
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : String(err);
          // Log which fields the backend flagged (PII-free) — turns a stuck user
          // into an observable signal (see the badge-creation funnel).
          const missing = /missing required parameters:\s*([a-z, ]+)/i.exec(msg)?.[1]?.trim();
          track('badge_create_error', missing ? { missing } : { status: String((err as { status?: number })?.status ?? '') });
          setCreateError(err instanceof Error ? err : new Error(String(err)));
        },
      }
    );
  }

  async function handleUpdate() {
    if (!editBadge || !name.trim() || locked) return;
    const data: { name?: string; description?: string; criteria?: string; image?: string; designJson?: string; designSvg?: string } = {};
    if (name.trim() !== editBadge.name) data.name = name.trim();
    if (description.trim() !== editBadge.description) data.description = description.trim();
    const oldCriteria = editBadge.criteria?.narrative;
    const oldCriteriaStr = Array.isArray(oldCriteria) ? oldCriteria.join('\n') : (oldCriteria || '');
    if (criteriaText.trim() !== oldCriteriaStr) data.criteria = criteriaText.trim();
    // If designer has a new image, include it; also capture the editable
    // Fabric JSON so the next edit can restore individual layers (legacy
    // badges with no designJson get one captured on first re-save).
    if (designerRef.current) {
      try {
        const exportedImage = await designerRef.current.onExport();
        if (exportedImage) data.image = exportedImage;
        const exportedJson = designerRef.current.onExportJSON();
        if (exportedJson) data.designJson = exportedJson;
        // Send the SVG whenever the designer is present so the server can sync
        // it: a design WITH tokens is stored (personalized images), and one
        // WITHOUT tokens clears any stale SVG (reverts to the class image).
        const exportedSvg = await designerRef.current.onExportSVG();
        if (typeof exportedSvg === 'string') data.designSvg = exportedSvg;
      } catch { /* user didn't design a new image, skip */ }
    }
    const shareChanged = shareText !== (editBadge.shareText || '');
    const badgeChanged = Object.keys(data).length > 0;
    if (!badgeChanged && !shareChanged) { closeModal(); return; }

    const badgeIdForApi = badgeGuid(editBadge);
    const tasks: Promise<unknown>[] = [];
    if (badgeChanged) {
      tasks.push(new Promise((resolve) => {
        updateBadge.mutate({ badgeId: badgeIdForApi, data }, { onSuccess: resolve, onError: resolve });
      }));
    }
    if (shareChanged) {
      tasks.push(new Promise((resolve) => {
        updateShareText({ badgeId: badgeIdForApi, text: shareText }, { onSuccess: resolve, onError: resolve });
      }));
    }
    await Promise.all(tasks);
    closeModal();
  }

  function handleDelete(badge: Badge) {
    setModalError(null);
    // Frontend safety check — block deletion if any awards exist for this badge.
    // Backend (Badge_delete.mjs) enforces the same rule; this just gives users
    // a clear, immediate explanation instead of waiting for the API round-trip.
    const awardCount = awardsData?.count ?? awardsData?.items.length ?? 0;
    if (awardCount > 0) {
      setModalError(
        t('badges.cannotDeleteAwards', "This badge has been awarded to {{count}} recipient(s), so it can't be deleted yet. Individual awards can't be removed on your own right now — please contact support (badges.ninja/support) and we'll revoke them for you.", { count: awardCount })
      );
      return;
    }
    setConfirmAction({
      title: t('badges.deleteTitle', 'Delete Badge'),
      message: t('badges.deleteMessage', 'Are you sure you want to delete "{{name}}"? This cannot be undone.', { name: badge.name }),
      onConfirm: () => {
        setConfirmAction(null);
        deleteBadge(badgeGuid(badge), {
          onSuccess: () => { closeModal(); },
          onError: (err) => {
            setModalError(
              t('badges.deleteFailed', 'Delete failed: {{message}}', { message: (err as Error).message || 'unknown error' })
            );
          },
        });
      },
    });
  }

  const isLoading = issuersLoading || badgesLoading;

  // Required fields to create a badge, surfaced LIVE in the editor so a user
  // sees what's still missing while they design — instead of hitting a cold
  // block / 400 the moment they click "Create" (the #1 first-run friction:
  // heavy designer engagement that never completes, usually stalling on the
  // Description / Criteria metadata). Keeps the Create button's enable state in
  // lock-step with the handleCreate guards so a ready-looking button always works.
  const createRequirements = [
    { ok: !!selectedIssuer, label: t('badges.issuer', 'Issuer') },
    { ok: !!name.trim(), label: t('badges.name', 'Name') },
    { ok: !!description.trim(), label: t('badges.description', 'Description') },
    { ok: !!criteriaText.trim(), label: t('badges.criteriaLabel', 'Criteria') },
  ];
  const missingRequirements = createRequirements.filter(r => !r.ok).map(r => r.label);

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('badges.title', 'Badges')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('badges.subtitle', 'Create and manage your badge designs.')}</p>
          </div>
          <button data-testid="open-create-badge" onClick={openCreate} disabled={atBadgeLimit}
            title={atBadgeLimit ? "You've reached the badge limit on your plan" : undefined}
            className="rounded-xl bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600">
            {t('badges.create', 'Create Badge')}
          </button>
        </div>

        <LimitWarning usage={usage} resource="badges" />

        {/* Inline help — how to design certificates (dismissible) */}
        {!certTipDismissed && badgeList.length > 0 && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-primary-100 bg-primary-50/70 px-4 py-3">
            <div className="flex-none mt-0.5 grid h-8 w-8 place-items-center rounded-lg bg-primary-100 text-primary-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4m5 4H6a2 2 0 01-2-2V6a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">{t('badges.certTipTitle', 'Design your certificates')}</p>
              <p className="mt-0.5 text-xs text-gray-600">
                {t('badges.certTipBody', 'Each badge can have a custom PDF certificate that recipients download — personalized with their name, your branding, and a verification QR code. Click the')}{' '}
                <span className="font-semibold text-primary-700">{t('badges.certificate', 'Certificate')}</span>{' '}
                {t('badges.certTipBodyEnd', 'button on any badge to start.')}
              </p>
            </div>
            <button onClick={dismissCertTip} className="flex-none rounded-lg p-1 text-gray-400 hover:bg-white hover:text-gray-600 transition-colors" aria-label={t('common:buttons.dismiss', 'Dismiss')}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
            </button>
          </div>
        )}

        {/* Issuer filter */}
        <div className="mb-6">
          <select
            value={issuerFilter}
            onChange={e => setIssuerFilter(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          >
            <option value="all">{t('badges.allIssuers', 'All Issuers')}</option>
            {issuerList.map((i: Issuer) => (
              <option key={i.id} value={i.id}>{i.name}</option>
            ))}
          </select>
        </div>

        {/* Badge grid */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 rounded-2xl border border-gray-200 bg-white animate-pulse" />
            ))}
          </div>
        ) : (filteredBadges.length > 0 || drafts.length > 0) ? (
          <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {/* Drafts first — badges-in-progress, resumable, marked "Draft". */}
            {issuerFilter === 'all' && drafts.map((d) => (
              <div
                key={`draft-${d.id}`}
                role="button"
                tabIndex={0}
                data-testid={`draft-card-${d.id}`}
                onClick={() => openDraft(d.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') openDraft(d.id); }}
                className="group relative flex cursor-pointer flex-col text-left rounded-2xl border border-dashed border-primary-300 bg-white shadow-card hover:border-primary-400 hover:shadow-md transition-all overflow-hidden"
              >
                <span className="absolute left-3 top-3 z-10 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white shadow">{t('badges.draftTag', 'Draft')}</span>
                <button
                  type="button"
                  data-testid={`draft-delete-${d.id}`}
                  onClick={(e) => { e.stopPropagation(); setConfirmAction({ title: t('badges.deleteDraftTitle', 'Delete draft?'), message: t('badges.deleteDraftMsg', 'This badge draft will be permanently removed.'), onConfirm: () => deleteDraft(d.id) }); }}
                  className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1 text-gray-400 opacity-0 shadow hover:text-red-600 group-hover:opacity-100"
                  title={t('badges.delete', 'Delete')}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
                <div className="relative aspect-square w-full bg-gradient-to-br from-primary-50/50 via-white to-gray-50 flex items-center justify-center p-6">
                  {d.image
                    ? <img src={d.image} alt={d.name} className="max-h-full max-w-full object-contain" loading="lazy" />
                    : <svg className="h-16 w-16 text-primary-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>}
                </div>
                <div className="px-4 pt-4 pb-3 border-t border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 truncate" title={d.name}>{d.name || t('badges.untitledDraft', 'Untitled draft')}</h3>
                  <p className="text-xs text-primary-600 mt-0.5">{t('badges.continueEditing', 'Continue editing →')}</p>
                </div>
              </div>
            ))}
            {filteredBadges.map((badge: Badge) => {
              const guid = badge.id.split('/').pop() || badge.id;
              const criteriaArr = Array.isArray(badge.criteria?.narrative)
                ? badge.criteria.narrative
                : (badge.criteria?.narrative
                    ? badge.criteria.narrative.split(/\.\s|\n/).map(s => s.trim()).filter(Boolean)
                    : []);
              return (
                <div key={badge.id} className="relative">
                <button
                  data-testid={`badge-card-${guid}`}
                  onClick={() => openEdit(badge)}
                  className="group flex flex-col w-full text-left rounded-2xl border border-gray-200 bg-white shadow-card hover:border-primary-300 hover:shadow-md transition-all overflow-hidden"
                >
                  {/* Hero image — soft gradient backdrop, badge centered */}
                  <div className="relative aspect-square w-full bg-gradient-to-br from-gray-50 via-white to-primary-50/40 flex items-center justify-center p-6">
                    <img
                      src={badge.image}
                      alt={badge.name}
                      className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105"
                      onError={e => { (e.target as HTMLImageElement).src = '/assets/badge-templates/Template-000.png'; }}
                    />
                  </div>

                  {/* Body */}
                  <div className="px-4 pt-4 pb-3 border-t border-gray-100 space-y-2.5">
                    <div>
                      <h3 className="text-base font-bold text-gray-900 truncate" title={badge.name}>{badge.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
                        {badge.issuer?.verified && (
                          <svg className="h-3.5 w-3.5 flex-shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span className="truncate">{badge.issuer?.name || '—'}</span>
                      </div>
                    </div>

                    {badge.description && (
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2" title={badge.description}>
                        {badge.description}
                      </p>
                    )}

                    {/* Metric pills */}
                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                      {criteriaArr.length > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600">
                          <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span><span className="font-semibold text-gray-800">{criteriaArr.length}</span> {criteriaArr.length === 1 ? 'criterion' : 'criteria'}</span>
                        </span>
                      )}
                      {badge.shareText && (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-primary-50 border border-primary-100 px-2 py-0.5 text-[11px] text-primary-700"
                          title="Custom share text set"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                          </svg>
                          Share text
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Footer: GUID + edit icon */}
                  <div className="mt-auto px-4 py-2.5 bg-gray-50/60 border-t border-gray-100 flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] text-gray-400 truncate" title={badge.id}>
                      {guid}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-gray-500 group-hover:text-primary-600 group-hover:bg-white transition-colors"
                      aria-hidden="true"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 7.125l-3-3M3 21h18" />
                      </svg>
                      Edit
                    </span>
                  </div>
                </button>
                {/* Certificate designer action — a clear filled CTA, overlaid so
                    it isn't nested in the card button. */}
                <button
                  onClick={(e) => { e.stopPropagation(); setCertBadge(badge); }}
                  className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-[12px] font-semibold text-white shadow-md ring-1 ring-primary-700/20 hover:bg-primary-700 active:scale-[0.97] transition-all"
                  title={t('badges.designCertificate', 'Design the PDF certificate recipients download')}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4m5 4H6a2 2 0 01-2-2V6a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2z" />
                  </svg>
                  {t('badges.certificate', 'Certificate')}
                </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-card">
            <p className="text-sm text-gray-400">{t('badges.noBadges', 'No badges yet. Create your first badge to get started.')}</p>
          </div>
        )}
      </div>

      {/* Create / edit badge modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4">
          <div className="fixed inset-0 bg-black/40" onClick={closeModal} />
          <div className="relative z-10 w-full h-full md:h-[95vh] md:max-w-[95vw] rounded-none md:rounded-2xl bg-white shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0 gap-3">
              <h2 className="text-lg font-bold text-gray-900">{editBadge ? t('badges.editBadge', 'Edit Badge') : t('badges.createBadge', 'Create Badge')}</h2>
              <div className="flex items-center gap-3">
                {editBadge && (
                  <button
                    onClick={() => setCertBadge(editBadge)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
                    title={t('badges.designCertificate', 'Design the PDF certificate recipients download')}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h4m5 4H6a2 2 0 01-2-2V6a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2z" />
                    </svg>
                    {t('badges.certificate', 'Certificate')}
                  </button>
                )}
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
              </div>
            </div>
            {editBadge && hasAwards && (
              <div className="mx-6 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
                {t('badges.editableWithAwards', 'This badge has {{count}} issued award(s). You can still edit it — changes apply to future awards only; already-issued awards keep their original design.', { count: awardsData?.count ?? awardsData?.items.length ?? 0 })}
              </div>
            )}
            {/* Full-page 3-column editor; badge details live under the canvas. */}
            <div className="flex-1 min-h-0">
              <BadgeDesigner
                ref={designerRef}
                initialImageUrl={editBadge?.image}
                initialDesignJson={editBadgeFull?.designJson ?? pendingDesignJson}
                autoOpenTemplates={!editBadge && !pendingDesignJson}
                accountTemplates={templatesData?.items}
                onUseAccountTemplate={async (id) => {
                  try { const tpl = await api.getTemplate(id); if (tpl.designJson) designerRef.current?.loadDesign(tpl.designJson); } catch { /* ignore */ }
                }}
                detailsSlot={
                  <div className="w-full space-y-4 px-8 py-6">
                <fieldset disabled={locked} className="disabled:opacity-60">
                  <div className="grid gap-x-8 gap-y-4 md:grid-cols-[1fr_1.3fr]">
                  <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('badges.issuer', 'Issuer')}</label>
                    <IssuerPicker
                      issuers={verifiedIssuers}
                      selected={selectedIssuer}
                      onSelect={setSelectedIssuer}
                      placeholder={t('badges.selectIssuer', 'Select an issuer...')}
                      disabled={!!editBadge}
                    />
                    {verifiedIssuers.length === 0 && (
                      <p className="mt-1 text-xs text-amber-600">{t('badges.noVerifiedIssuers', 'No verified issuers. Please verify an issuer first.')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('badges.name', 'Name')} <span className="text-red-500" title={t('badges.requiredField', 'Required')}>*</span></label>
                    <input
                      type="text"
                      data-testid="badge-name-input"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={t('badges.namePlaceholder', 'Badge name')}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('badges.description', 'Description')} <span className="text-red-500" title={t('badges.requiredField', 'Required')}>*</span></label>
                    <textarea
                      data-testid="badge-description-input"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      rows={4}
                      placeholder={t('badges.descriptionPlaceholder', 'Describe what this badge represents...')}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t('badges.criteriaLabel', 'Criteria')} <span className="text-red-500" title={t('badges.requiredField', 'Required')}>*</span></label>
                    <textarea
                      data-testid="badge-criteria-input"
                      value={criteriaText}
                      onChange={e => setCriteriaText(e.target.value)}
                      rows={5}
                      placeholder={t('badges.criteriaPlaceholder', 'One criterion per line...')}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                    />
                  </div>
                  </div>
                  <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-medium text-gray-700">
                        {t('badges.shareText', 'Share Text')}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowPlaceholders(v => !v)}
                          className="text-[11px] text-primary-600 hover:text-primary-700 hover:underline"
                        >
                          {t('badges.shareTextInsert', '+ Insert placeholder')}
                        </button>
                        {showPlaceholders && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setShowPlaceholders(false)} />
                            <div className="absolute right-0 top-full mt-1 z-40 w-72 rounded-lg border border-gray-200 bg-white shadow-lg">
                              <div className="border-b border-gray-100 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                {t('badges.shareTextInsertTitle', 'Insert at cursor')}
                              </div>
                              <ul className="max-h-64 overflow-y-auto py-1">
                                {SHARE_PLACEHOLDERS.map(p => (
                                  <li key={p.token}>
                                    <button
                                      type="button"
                                      onClick={() => insertPlaceholder(p.token)}
                                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                                    >
                                      <div className="text-xs font-medium text-gray-800">{p.label}</div>
                                      <div className="text-[11px] font-mono text-primary-600">{p.token}</div>
                                      <div className="text-[10px] text-gray-400 mt-0.5">e.g. {p.example}</div>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1.5 leading-snug">
                      {t('badges.shareTextHelp', 'Pre-filled text when recipients click "Share" on their award (LinkedIn, Twitter, email). Use {{placeholders}} for per-award personalization — they\'re replaced at share time with each recipient\'s name, the badge URL, etc.')}
                    </p>
                    <textarea
                      ref={shareTextRef}
                      value={shareText}
                      onChange={e => { setShareText(e.target.value); setShareTextTouched(true); }}
                      rows={5}
                      placeholder={t('badges.shareTextPlaceholder', 'Type your message and use Insert to add {{recipient_name}}, {{badge_name}}, {{award_url}}, ...')}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 font-mono"
                    />
                    {shareTextTouched && !editBadge && (
                      <button
                        type="button"
                        onClick={() => { setShareText(defaultShareText); setShareTextTouched(false); }}
                        className="mt-1 text-[11px] text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {t('badges.shareTextReset', 'Reset to default template')}
                      </button>
                    )}
                  </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                      <div className="flex items-start gap-2">
                        <svg className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                        <div className="min-w-0 text-[11px] text-gray-700 leading-snug">
                          <p className="font-semibold text-gray-900 mb-1">{t('badges.tipShareTitle', 'Share text = free distribution')}</p>
                          <p>{t('badges.tipShareBody', 'Every recipient who shares their award broadcasts your brand to their LinkedIn / Twitter / email network. A polished, specific share text turns each award into a marketing moment — generic copy gets ignored, on-brand copy spreads. Worth a few extra minutes.')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </fieldset>

                {modalError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-start justify-between gap-2">
                    <span className="leading-snug">{modalError}</span>
                    <button type="button" onClick={() => setModalError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0 leading-none">&times;</button>
                  </div>
                )}

                <ActionErrorPanel
                  error={createError}
                  context={{ resource: 'badge', action: editBadge ? 'update' : 'create', payload: lastCreatePayload ?? undefined }}
                  onDismiss={() => setCreateError(null)}
                />

                {!editBadge && missingRequirements.length > 0 && (
                  <div data-testid="badge-create-requirements" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                    <svg className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span className="leading-snug">
                      {t('badges.stillNeeded', 'To create this badge, still add:')}{' '}
                      <span className="font-semibold">{missingRequirements.join(', ')}</span>
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                  {editBadge ? (
                    <button onClick={() => handleDelete(editBadge)} className="rounded-lg border border-red-100 px-3 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors">
                      {t('badges.delete', 'Delete')}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button type="button" data-testid="save-draft" disabled={draftBusy} onClick={() => saveDraft(true)} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors">
                        {draftBusy ? t('badges.savingDraft', 'Saving…') : t('badges.saveDraft', 'Save Draft')}
                      </button>
                      {draftSaved && (
                        <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600">
                          <input type="checkbox" data-testid="autosave-toggle" checked={autoSave} onChange={e => setAutoSave(e.target.checked)} className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-400" />
                          {t('badges.autoSave', 'Auto-save')}
                        </label>
                      )}
                      {draftSaved && <span className="text-[11px] text-gray-400">{autoSave ? t('badges.autoSaveOn', 'Saving automatically') : t('badges.draftSavedAt', 'Draft saved')}</span>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={closeModal} className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                      {t('common:buttons.cancel', 'Cancel')}
                    </button>
                    <button
                      data-testid="submit-badge"
                      onClick={editBadge ? handleUpdate : handleCreate}
                      // Description is intentionally NOT button-gated: handleCreate's guard
                      // shows a specific inline error on click (C1 exception-triage contract),
                      // and the "still needed" hint + required markers surface it beforehand.
                      disabled={(editBadge ? (updateBadge.isPending || updatingShare || locked) : (creating || !criteriaText.trim())) || !name.trim() || !selectedIssuer}
                      className="rounded-lg bg-primary-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {editBadge
                        ? ((updateBadge.isPending || updatingShare) ? t('badges.updating', 'Updating...') : t('badges.updateBadge', 'Update Badge'))
                        : (creating ? t('badges.creating', 'Creating...') : t('badges.create', 'Create Badge'))}
                    </button>
                  </div>
                </div>

                </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={t('badges.confirmDelete', 'Delete')}
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />

      {certBadge && (
        <CertificateDesigner
          badge={{ id: certBadge.id, name: certBadge.name, issuerName: certBadge.issuer?.name, issuerId: certBadge.issuer?.id }}
          onClose={() => setCertBadge(null)}
        />
      )}
    </div>
  );
}
